import React, { useEffect, useMemo } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  TextareaAutosize,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import {
  atom,
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import { ChangeEvent, useCallback, useState } from 'react'
import {
  contentServiceDelete,
  contentServiceGet,
  contentServicePost,
} from 'infrastructure/contentService'
import { GoogleLoginButton, googleUserAtom } from 'infrastructure/googleDrive'
import { HttpError } from 'infrastructure/api'
import { keyTest, objectTest } from 'shared/utils'
import { ValidationError } from './types'

const GAuthHeaderKey = 'GAuthorization'

const pathAtom = atom('')
const routeParamsAtom = atom<Record<string, string>>({})
const requestMethodAtom = atom('GET')
const resultsAtom = atom<{ path: string; result: unknown }[]>([])
const requestBodyAtom = atom<Record<string, unknown>>({})
const requestHeadersAtom = atom<Record<string, string>>({})
const requestQueryParamsAtom = atom<Record<string, string>>({})
const pathWithParamsAtom = atom((get) => {
  const path = get(pathAtom)
  const routeParams = get(routeParamsAtom)
  const queryParams = get(requestQueryParamsAtom)
  const fullPath = path
    .replace(/<([^>]+)>/g, (_match, param) => String(routeParams[param] ?? ''))
    .replace(/\{([^}]+)\}/g, (_match, inner: string) =>
      inner
        .split(':')
        .map((param) => String(routeParams[param] ?? ''))
        .join(':'),
    )
  const qParams = new URLSearchParams(queryParams).toString()
  if (!qParams) return fullPath
  return `${fullPath}?${qParams}`
})

export function Playground() {
  const [path, setPath] = useAtom(pathAtom)

  return (
    <Box>
      <Box>Content Service Playground</Box>
      <Box padding="20px">
        <RequestMethodToggle />
        <Spacer />
        <Box>
          <TextField
            label="Path"
            value={path}
            fullWidth
            onChange={(e) => setPath(e.target.value)}
          />
        </Box>
        <Spacer />
        <RouteParams />
        <Spacer />
        <RequestBody />
        <Spacer />
        <RequestQueryParams />
        <Spacer />
        <RequestHeaders />
        <Spacer />
        <CurrentRequest />
        <Spacer />
        <Results />
      </Box>
    </Box>
  )
}

function RouteParams() {
  const [routeParams, setRouteParams] = useAtom(routeParamsAtom)
  const path = useAtomValue(pathAtom)
  if (!path) return null
  const angleParams = [...path.matchAll(/<([^>]+)>/g)].map((m) => m[1])
  const curlyParams = [...path.matchAll(/\{([^}]+)\}/g)].flatMap((m) =>
    m[1].split(':'),
  )
  const params = [...angleParams, ...curlyParams]
  if (!params.length) return null
  const updateParamFunc =
    (param: string) => (e: ChangeEvent<HTMLInputElement>) => {
      setRouteParams({
        ...routeParams,
        [param]: e.target.value,
      })
    }
  return (
    <Box>
      <Box>Route Params</Box>
      <Box padding="5px">
        {params.map((p) => {
          return (
            <Box key={p}>
              <TextField
                label={p}
                fullWidth
                value={String(routeParams[p] ?? '')}
                onChange={updateParamFunc(p)}
              />
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

function RequestBody() {
  const [bodyString, setBodyString] = useState('')
  const requestMethod = useAtomValue(requestMethodAtom)
  const setRequestBody = useSetAtom(requestBodyAtom)
  if (requestMethod !== 'POST') return null
  const onChange = ({ target }: ChangeEvent<HTMLTextAreaElement>) => {
    const { value, selectionStart, selectionEnd } = target
    const formatted = value.replace("'", '"')
    setBodyString(formatted)
    requestAnimationFrame(() => {
      target.setSelectionRange(selectionStart, selectionEnd)
    })
    try {
      const asJson = JSON.parse(formatted)
      const asString = JSON.stringify(asJson, null, 2)
      if (asString === bodyString) return
      setRequestBody(asJson)
      setBodyString(asString)
    } catch (_) {}
  }
  return (
    <Box>
      <Box>Request Body</Box>
      <Box padding="5px">
        <TextareaAutosize
          style={{ width: '100%' }}
          value={bodyString}
          onChange={onChange}
        />
      </Box>
    </Box>
  )
}

export function RequestMethodToggle() {
  const [requestMethod, setRequestMethod] = useAtom(requestMethodAtom)
  const methods = ['GET', 'POST', 'DELETE']
  const handleChange = (
    _: React.MouseEvent<HTMLElement>,
    newValue: string | null,
  ) => {
    if (newValue) setRequestMethod(newValue)
  }

  return (
    <ToggleButtonGroup
      exclusive
      value={requestMethod}
      onChange={handleChange}
      aria-label="HTTP method selector"
      sx={{
        flexWrap: 'wrap',
        gap: 0.5,
        '& .MuiToggleButton-root': {
          textTransform: 'none',
          letterSpacing: 0.3,
          fontWeight: 600,
          borderRadius: 2,
          px: 2,
        },
      }}
    >
      {methods.map((method) => (
        <ToggleButton
          size="small"
          key={method}
          value={method}
          aria-label={method}
          sx={{
            borderColor: methodAccent(method, 0.3),
            '&.Mui-selected': {
              backgroundColor: methodAccent(method, 0.18),
              borderColor: methodAccent(method, 0.9),
            },
            '&:hover': {
              backgroundColor: methodAccent(method, 0.12),
            },
          }}
        >
          {method}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

function RequestQueryParams() {
  const [addingQueryParams, setAddingQueryParams] = useState(false)
  const [queryParams, setQueryParams] = useAtom(requestQueryParamsAtom)
  const [newParam, setNewParam] = useState('')

  if (!addingQueryParams) {
    return (
      <Box>
        <Button onClick={() => setAddingQueryParams(true)}>
          Add Query Params
        </Button>
      </Box>
    )
  }

  const removeParam = (param: string) => {
    setQueryParams((p) => {
      const { [param]: _, ...rest } = p
      return rest
    })
  }

  return (
    <Box>
      {Object.entries(queryParams).map(([key, value]) => {
        return (
          <Box key={key}>
            <TextField
              label={key}
              value={value}
              onChange={(e) => {
                setQueryParams({
                  ...queryParams,
                  [key]: e.target.value,
                })
              }}
            />
            <Button onClick={() => removeParam(key)}>X</Button>
          </Box>
        )
      })}
      <Box>
        <TextField
          label="New Param"
          value={newParam}
          onChange={(e) => {
            setNewParam(e.target.value)
          }}
        />
        <Button
          onClick={() => {
            setQueryParams((p) => ({ ...p, [newParam]: '' }))
            setNewParam('')
          }}
        >
          Add
        </Button>
      </Box>
    </Box>
  )
}

function RequestHeaders() {
  const [useGoogleAuth, setUseGoogleAuth] = useState(false)
  const setRequestHeaders = useSetAtom(requestHeadersAtom)
  const [gAuth, resetGAuth] = useAtom(googleUserAtom)

  useEffect(() => {
    if (useGoogleAuth) {
      setRequestHeaders((prev) => {
        return {
          ...prev,
          [GAuthHeaderKey]: gAuth.credential?.accessToken ?? '',
        }
      })
    } else {
      setRequestHeaders((prev) => {
        return Object.entries(prev).reduce((acc, [key, value]) => {
          if (key === GAuthHeaderKey) return acc
          return {
            ...acc,
            [key]: value,
          }
        }, {})
      })
    }
  }, [useGoogleAuth, setRequestHeaders, gAuth])

  if (!useGoogleAuth) {
    return (
      <Box>
        <Button onClick={() => setUseGoogleAuth(true)}>Use Google Auth</Button>
      </Box>
    )
  }
  return (
    <Box>
      <GoogleLoginButton
        hideWhenLoggedIn={true}
        LoggedInComponent={() => (
          <>
            <Button onClick={() => setUseGoogleAuth(false)}>
              Don't Use GAuth
            </Button>
            <Button onClick={resetGAuth}>Reset GAuth</Button>
          </>
        )}
      />
    </Box>
  )
}

function methodAccent(method: string, alpha = 1) {
  const base = {
    GET: '28, 125, 49',
    POST: '25, 118, 210',
    PUT: '255, 143, 0',
    PATCH: '102, 60, 0',
    DELETE: '211, 47, 47',
    HEAD: '81, 45, 168',
    OPTIONS: '0, 105, 92',
  } as Record<string, string>

  const rgb = base[method] || '97, 97, 97'
  return `rgba(${rgb}, ${alpha})`
}

function CurrentRequest() {
  const requestMethod = useAtomValue(requestMethodAtom)
  const doIt = useMakeRequest()
  const pathWithParams = useAtomValue(pathWithParamsAtom)
  const requestBody = useAtomValue(requestBodyAtom)
  const headers = useAtomValue(requestHeadersAtom)

  const headersString = useMemo(() => {
    if (!Object.keys(headers).length) return ''
    return Object.entries(headers)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n')
  }, [headers])

  if (!pathWithParams) return null

  return (
    <Box>
      <Box>
        Current Request <Button onClick={doIt}>Do It</Button>
      </Box>
      <Box padding="10px">{`${requestMethod} ${pathWithParams}`}</Box>
      {requestMethod === 'POST' && (
        <Box>
          <TextareaAutosize
            disabled={true}
            style={{ width: '100%' }}
            value={`-d ${JSON.stringify(requestBody, null, 2)}`}
          />
        </Box>
      )}
      {headersString && (
        <Box>
          <TextareaAutosize
            disabled={true}
            style={{ width: '100%' }}
            value={`HEADERS\n${headersString}`}
          />
        </Box>
      )}
    </Box>
  )
}

function Spacer() {
  return <Box padding="10px" />
}

function Results() {
  const [results, setResults] = useAtom(resultsAtom)
  if (!results.length) return null
  return (
    <Box>
      <Box>
        Results <Button onClick={() => setResults([])}>Clear</Button>
      </Box>
      <Box padding="10px">
        {results.map(({ path, result }, idx) => {
          return (
            <Box key={`${path}:${idx}`}>
              <Accordion>
                <AccordionSummary>{path}</AccordionSummary>
                <AccordionDetails>
                  <TextareaAutosize
                    disabled={true}
                    style={{ width: '100%' }}
                    value={JSON.stringify(result, null, 2)}
                  />
                  <FlowDataError result={result} />
                </AccordionDetails>
              </Accordion>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

function useMakeRequest() {
  const resetGuser = useSetAtom(googleUserAtom)
  const setResults = useSetAtom(resultsAtom)
  const requestMethod = useAtomValue(requestMethodAtom)
  const path = useAtomValue(pathWithParamsAtom)
  const body = useAtomValue(requestBodyAtom)
  const headers = useAtomValue(requestHeadersAtom)
  return useCallback(async () => {
    if (!path) return
    let result: unknown
    switch (requestMethod) {
      case 'GET': {
        result = await contentServiceGet({ path, optionOverrides: { headers } })
        break
      }
      case 'POST': {
        result = await contentServicePost({
          path,
          body,
          optionOverrides: { headers },
        })
        break
      }
      case 'DELETE': {
        result = await contentServiceDelete({
          path,
          optionOverrides: { headers },
        })
        break
      }
    }
    setResults((results) => [
      {
        path: `${requestMethod} ${path}`,
        result,
      },
      ...results,
    ])
    if (
      result &&
      (result as HttpError).status === 401 &&
      ((result as HttpError).data as any)?.error === 'token_expired'
    ) {
      resetGuser()
    }
  }, [requestMethod, body, path, setResults, headers])
}

function isValidationError(error: unknown): error is ValidationError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error as ValidationError).code === 'ValidationError'
  )
}

function FlowDataError({ result }: { result: unknown }) {
  const dataKey = 'data'
  if (!objectTest(result) || !keyTest(result, dataKey)) return null
  const data = result[dataKey]
  const errorKey = 'errors'
  if (!objectTest(data) || !keyTest(data, errorKey)) return null
  const errors = data[errorKey] as unknown
  if (!Array.isArray(errors) || !errors.length) return null
  const validationErrors = errors.filter(isValidationError)
  if (!validationErrors.length) return null
  const errorsString = validationErrors.map((e) => e.detail).join('\n')
  return (
    <TextareaAutosize
      disabled={true}
      value={errorsString}
      style={{ width: '100%' }}
    />
  )
}
